import * as React from 'react';
import classNames from 'classnames';
import { AxiosError } from 'axios';
import { Loader } from 'semantic-ui-react';
import { CustomerEntity } from 'Models/Entities';
import AdverseAlertsCases from './AdverseAlertsCases';
import AdverseAlertsBusinessSummary from './AdverseAlertsBusinessSummary';
import AdverseAlertsAbrSummary from './AdverseAlertsAbrSummary';
import AdverseAlertsAsicUpdates from './AdverseAlertsAsicUpdates';
import AdverseAlertsLicences from './AdverseAlertsLicences';
import AdverseAlertsAdjudications from './AdverseAlertsAdjudications';
import AdverseAlertsInsolvencies from './AdverseAlertsInsolvencies';
import AdverseAlertsAtoDefaults from './AdverseAlertsAtoDefaults';
import ButtonAsyncState from 'Views/Components/Button/ButtonAsyncState';
import { Display, Sizes } from 'Views/Components/Button/Button';
import { store } from 'Models/Store';
import { confirmModalAsync } from 'Views/Components/Modal/ModalUtils';
import useCustomerAlaresReport from 'Hooks/Api/useCustomerAlaresReport';
import { AlaresReportUtil } from 'Models/Interfaces/AlaresReport';
import alertToast from 'Util/ToastifyUtils';
import { requestCustomerAlaresReport } from 'Hooks/Api/apiAlaresReport';

const AdverseAlertsReport = (props: {
	customer: CustomerEntity,
}) => {
	const { customer } = props;

	const reqCustomerAlaresReportOptions = React.useMemo(() => (
		{
			customerId: customer.id
		}
	), [customer.id]);

	const reqCustomerAlaresReport = useCustomerAlaresReport(reqCustomerAlaresReportOptions);

	const createNewReport = React.useCallback(async () => {
		const modal = confirmModalAsync(NewReportModal.title, NewReportModal.content);

		try { await modal.result(); } catch (e) { return; }

		try {
			await requestCustomerAlaresReport(customer.id);
		} catch (err) {
			const axiosErr = (err as AxiosError);
			alertToast(axiosErr?.response?.data ?? "Error requesting new alares report", 'error');
		} finally {
			modal.close();
		}


	}, [customer.id]);


	// Determine view.
	// 1. Loading
	if (reqCustomerAlaresReport.type === 'loading') return <LoadingView />

	// 2. No stored alares report
	if (reqCustomerAlaresReport.error || !reqCustomerAlaresReport.data || reqCustomerAlaresReport.data.report.created_at === null) {

		return (
			<div className={classNames('adverse-alerts-report')}>
				<div className="error-container">
					<h4>
						Adverse alerts are currently being generated.<br />
						If this persists for multiple days please contact Access Intell.
					</h4>
					{store.userType === 'SUPER_USER' && (
						<ButtonAsyncState
							display={Display.Solid}
							waitingText="Confirming..."
							onPress={createNewReport}
						>
							Retrieve Alares Report
						</ButtonAsyncState>
					)}
				</div>
			</div>
		)
	}

	const alaresReport = reqCustomerAlaresReport.data.report;
	const atoDefaultRecords = reqCustomerAlaresReport.data.atoDefaults ?? [];

	const allRelevantDates = [
		...atoDefaultRecords.filter(c => c.fileDate != null).map(x => x.fileDate ?? new Date()),
		AlaresReportUtil.getLastUpdated(alaresReport)
	].filter(x => x != null);


	const lastUpdated = allRelevantDates.length > 0 ? allRelevantDates[0] : undefined;

	// 3. Stored alares report
	return (
		<div className={classNames('adverse-alerts-report')}>
			<h3>
				Adverse alerts
				{store.userType === 'SUPER_USER' && (
					<ButtonAsyncState
						sizes={Sizes.Small}
						display={Display.Outline}
						waitingText="Confirming..."
						onPress={createNewReport}
						buttonProps={{
							style: { float: 'right' },
						}}
					>
						Retrieve New Alares Report
					</ButtonAsyncState>
				)}
			</h3>
			<AdverseAlertsBusinessSummary customer={customer} alaresReport={alaresReport} lastUpdated={lastUpdated ?? undefined} />
			<AdverseAlertsAbrSummary alaresReport={alaresReport} />
			<AdverseAlertsCases cases={AlaresReportUtil.getOrderedCasesList(alaresReport)} />
			<AdverseAlertsAsicUpdates documents={AlaresReportUtil.getOrderedAsicDocuments(alaresReport)} />
			<AdverseAlertsAtoDefaults records={reqCustomerAlaresReport.data.atoDefaults ?? []} />
			<AdverseAlertsLicences licences={AlaresReportUtil.getOrderedLicences(alaresReport)} />
			<AdverseAlertsAdjudications adjudications={AlaresReportUtil.getOrderedAdjudications(alaresReport)} />
			<AdverseAlertsInsolvencies insolvencies={AlaresReportUtil.getOrderedInsolvencies(alaresReport)} />
		</div>
	)
};

function LoadingView() {
	return (
		<div className={classNames('adverse-alerts-report', 'loading')}>
			<Loader inline active size="massive">Loading</Loader>
		</div>
	)
}

const NewReportModal = {
	title: 'Confirm Retrieve New Alares Report',
	content: (
		<>
			<b>Force the system to retrieve a new report from Alares.</b>
			<br />
			<i>This will delete any un-processed (likely stuck) Alares Reports to ensure the new report is used.</i>
		</>
	)
}

export default AdverseAlertsReport;
